import React, { PureComponent } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { store } from 'redux-rest-reducer';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import { Link } from 'react-router-dom';

import styled from 'styled-components';
import 'react-table/react-table.css';
import 'react-table-hoc-fixed-columns/lib/styles.css';

import { IconButton, LinkButton } from 'modules/list';
import { epochToDate } from 'utils';
import { get as getTeacherData } from 'actions/teachers';
import { Row, Button, Separator } from '@cuemath/comps';
import EditIcon from 'img/edit.png';
import { FORM_ACTIONS } from 'constants/form';
import { COURSE_TYPE, ACCESS_TYPE } from 'constants/student-courses';
import { CardTableHeader } from 'modules/form';
import PropTypes from 'prop-types';

const ReactTableFixedColumns = withFixedColumns(ReactTable);

const TableWrapper = styled.div`
  margin: 5px;
`;
const ButtonWrapper = styled(Button)`
  margin: 0 5px;
`;

const GET_TEACHER_QUERY_PARAMS = {
  with_teacher_band: true,
};

class StudentCourses extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      studentCourses: [],
    };
  }

  static defaultProps = {
    studentCourses: [],
  };

  static propTypes = {
    actions: PropTypes.shape({
      getTeacherData: PropTypes.func.isRequired,
    }),
    onManageElectives: PropTypes.func.isRequired,
    onViewCourseLog: PropTypes.func.isRequired,
    openEditStudentCourseForm: PropTypes.func.isRequired,
    studentCourses: PropTypes.arrayOf(PropTypes.object),
    studentId: PropTypes.string.isRequired,
    teachers: PropTypes.shape({}).isRequired,
  };

  static getDerivedStateFromProps(props, state) {
    let { studentCourses, teachers } = props;
    const newState = {};

    studentCourses.forEach(data => {
      const { primary_teacher_id } = data;

      if (primary_teacher_id) {
        const teacherProfileGet = store.get(
          teachers,
          primary_teacher_id,
          GET_TEACHER_QUERY_PARAMS,
        );
        if (teacherProfileGet && teacherProfileGet.isLoaded) {
          data['teacher'] = teacherProfileGet.data;
          data['teacher_name'] = teacherProfileGet.data.name;
        }
      } else {
        data['teacher_name'] = 'NA';
      }
    });
    newState.studentCourses = studentCourses || [];
    return newState;
  }

  componentDidMount() {
    const { studentCourses, actions } = this.props;
    let teacherData = new Set();

    studentCourses &&
      Array.isArray(studentCourses) &&
      studentCourses.forEach(({ primary_teacher_id }) => {
        if (primary_teacher_id && !teacherData.has(primary_teacher_id)) {
          actions.getTeacherData(primary_teacher_id, GET_TEACHER_QUERY_PARAMS);
          teacherData.add(primary_teacher_id);
        }
      });
  }

  render() {
    const { studentCourses } = this.state;
    const {
      onViewCourseLog,
      openEditStudentCourseForm,
      openStudentUpgradeForm,
      openStudentTrialUpgradeForm,
      openTransferClassesForm,
      openStudentRevertUpgradePaymentLinkForm,
      onManageElectives,
      studentId,
    } = this.props;

    let columnsData = [
      {
        Header: 'Course Name',
        accessor: 'course_name',
      },
      {
        Header: 'Access Type',
        accessor: 'access_type',
      },
      {
        Header: 'Source',
        accessor: 'source',
      },
      {
        Header: 'Enrollment Type',
        accessor: 'enrollment_type',
        Cell: ({ value }) => (value ? `${value}` : '-'),
      },
      {
        Header: 'Teacher Name',
        accessor: 'teacher_name',
        Cell: row => {
          const {
            original: {
              primary_teacher_id: primaryTeacherId,
              teacher_name: teacherName,
            } = {},
          } = row || {};

          return primaryTeacherId ? (
            <Link
              to={{
                pathname: `/users/teachers/${primaryTeacherId}`,
              }}
              target="_blank"
            >
              {teacherName}
            </Link>
          ) : (
            teacherName
          );
        },
      },
      {
        Header: 'Start Date',
        accessor: 'class_start_date',
        Cell: ({ value }) => (value ? epochToDate(value, 'DD-MM-YYYY') : ''),
      },
      {
        Header: 'End Date',
        accessor: 'validity_till_date',
        Cell: ({ value }) => (value ? epochToDate(value, 'DD-MM-YYYY') : ''),
      },
      {
        Header: 'Status',
        accessor: 'state',
        width: 100,
      },
      {
        Header: 'Classes Format',
        accessor: 'class_ratio',
        width: 150,
        Cell: ({ value }) => (value ? `1 : ${value}` : 'NA'),
      },
      {
        Header: 'Classes Purchased',
        accessor: 'classes_purchased',
        width: 150,
      },
      {
        Header: 'Classes Remaining',
        accessor: 'classes_balance',
        Cell: ({ value }) => (value || value === 0 ? `${value}` : '-'),
        width: 150,
      },
      {
        Header: 'Log',
        accessor: 'id',
        width: 100,
        Cell: row => {
          return <LinkButton text="Details" onClick={() => onViewCourseLog(row.value)} />;
        },
      },
      {
        Header: 'Id',
        accessor: 'id',
        width: 350,
        Cell: ({ value }) => (value ? `${value}` : '-'),
      },
    ];

    columnsData.push({
      Header: 'Action',
      accessor: 'name',
      Cell: row => {
        if (
          row.original.cue_course_type !== COURSE_TYPE.ELECTIVES &&
          row.original.status !== 'EXP' &&
          !row.original.is_foundation
        ) {
          return (
            <Row justifyContent="space-around">
              <IconButton
                icon={EditIcon}
                onClick={() =>
                  openEditStudentCourseForm({
                    formType: FORM_ACTIONS.EDIT,
                    data: row.original,
                  })
                }
              />
            </Row>
          );
        } else {
          return '-';
        }
      },
    });

    columnsData.push({
      Header: '',
      accessor: 'upgrade',
      Cell: row => {
        if (row.original.access_type === ACCESS_TYPE.WITH_TEACHER) {
          return (
            <Row justifyContent="space-around">
              <Button
                compact
                onClick={() =>
                  openStudentUpgradeForm({
                    formType: FORM_ACTIONS.EDIT,
                    data: row.original,
                  })
                }
                label="Upgrade"
              />
            </Row>
          );
        } else {
          return '-';
        }
      },
    });

    columnsData.push({
      Header: '',
      accessor: 'trial-upgrade',
      Cell: row => {
        if (row.original.access_type === ACCESS_TYPE.WITH_TEACHER) {
          return (
            <Row justifyContent="space-around">
              <Button
                compact
                onClick={() =>
                  openStudentTrialUpgradeForm({
                    formType: FORM_ACTIONS.EDIT,
                    data: row.original,
                  })
                }
                label="Trial Upgrade"
              />
            </Row>
          );
        } else {
          return '-';
        }
      },
    });

    columnsData.push({
      Header: '',
      accessor: 'revert-upgrade',
      Cell: row => {
        if (row.original.access_type === ACCESS_TYPE.WITH_TEACHER) {
          return (
            <Row justifyContent="space-around">
              <Button
                compact
                onClick={() =>
                  openStudentRevertUpgradePaymentLinkForm({
                    formType: FORM_ACTIONS.EDIT,
                    data: row.original,
                  })
                }
                label="Revert Upgrade"
              />
            </Row>
          );
        } else {
          return '-';
        }
      },
    });

    columnsData.push({
      Header: '',
      accessor: 'transfer-classes',
      Cell: row => {
        return (
          <Row justifyContent="space-around">
            <Button
              compact
              onClick={() =>
                openTransferClassesForm({
                  formType: FORM_ACTIONS.EDIT,
                  data: row.original,
                })
              }
              label="Transfer Classes"
            />
          </Row>
        );
      },
    });

    const tableColumns = [
      {
        Header: ``,
        columns: columnsData,
      },
    ];

    return (
      <TableWrapper>
        <Separator transparent height="10" />
        <CardTableHeader alignItems="center" justifyContent="flex-end">
          <ButtonWrapper
            onClick={() => onManageElectives({ id: studentId })}
            label="Manage Elective Courses"
          />
        </CardTableHeader>
        <Separator transparent height="10" />
        <ReactTableFixedColumns
          data={studentCourses}
          columns={tableColumns}
          defaultPageSize={studentCourses.length !== 0 ? studentCourses.length : 4}
          style={{ maxHeight: 300, textAlign: 'center' }}
          className="-striped -highlight"
        />
      </TableWrapper>
    );
  }
}

export default connect(
  ({ teachers }) => ({
    teachers,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        getTeacherData,
      },
      dispatch,
    ),
  }),
)(StudentCourses);
